import { createRouter, createWebHistory } from 'vue-router';
import { validateAuthSession } from '@/utils/auth-session';
import { captureInviteCodeFromRouteQuery, stripInviteCodeFromQuery } from '@/utils/invite-code';
import { captureAcquisitionFromRoute } from '@/utils/analytics-acquisition';
import { loadRouteView, setupChunkLoadRecovery } from '@/utils/chunk-load.js';
import {
  consumeNavigationProgressSkip,
  finishOrHoldNavigationProgress,
  startNavigationProgress,
} from '@/composables/useNavigationProgress.js';
import { silenceQuietShellPrompts } from '@/utils/quiet-shell-prompts.js';

/** @param {() => Promise<{ default: import('vue').Component }>} importFn */
function lazyRoute(importFn) {
  return () => loadRouteView(importFn);
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: lazyRoute(() => import(/* webpackChunkName: "home" */ '@/views/home/index.vue')),
    meta: { keepAlive: true, feedScroll: true },
  },
  {
    path: '/explore',
    name: 'Explore',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "explore" */ '@/views/explore/index.vue')
    ),
    meta: { keepAlive: true, feedScroll: true },
  },
  {
    path: '/market/:id',
    name: 'MarketDetail',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "market-detail" */ '@/views/market/detail.vue')
    ),
    meta: { scrollToTop: true, navProgressCritical: true },
    async beforeEnter(to) {
      const { ensureMarketDetailCleanQuery } = await import(
        '@/domain/market/market-detail-symbols.js'
      );
      return ensureMarketDetailCleanQuery(to) || true;
    },
  },
  {
    path: '/sport',
    name: 'Sport',
    component: lazyRoute(() => import(/* webpackChunkName: "sport" */ '@/views/sport/index.vue')),
    meta: { titleKey: 'nav.sport', keepAlive: true, feedScroll: true },
  },
  {
    path: '/sport/futures/:marketId',
    name: 'SportFuturesMarketDetail',
    component: lazyRoute(() =>
      import(
        /* webpackChunkName: "sport-futures-market-detail" */ '@/views/sport/futures-market-detail.vue'
      )
    ),
  },
  {
    path: '/sport/live/:matchId',
    name: 'SportLiveMatchDetail',
    component: lazyRoute(() =>
      import(
        /* webpackChunkName: "sport-live-match-detail" */ '@/views/sport/live-match-detail.vue'
      )
    ),
    meta: { titleKey: 'nav.sport', navProgressCritical: true },
  },
  {
    path: '/crypto',
    name: 'Crypto',
    component: lazyRoute(() => import(/* webpackChunkName: "crypto" */ '@/views/crypto/index.vue')),
    meta: { titleKey: 'nav.crypto', keepAlive: true, feedScroll: true },
  },
  {
    path: '/crypto/updown/:id',
    name: 'CryptoUpdownDetail',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "crypto-updown-detail" */ '@/views/crypto/updown-detail.vue')
    ),
    /**
     * keepAlive + App key=`CryptoUpdownDetail:${sk}`：同系列分盘 replace id 时不销毁实例，才能 silent 切轮。
     * seriesKey 记在内存（非 URL）；进页前 beforeEnter 解析并 remember，避免挂载后换 key remount。
     * 离开页靠 Content onDeactivated → NavSession.reset，避免劫持其它路由。
     */
    meta: {
      titleKey: 'nav.crypto',
      scrollToTop: true,
      keepAlive: true,
      navProgressCritical: true,
    },
    async beforeEnter(to) {
      const { ensureCryptoUpdownDetailEnter } = await import(
        '@/composables/useCryptoUpdownDetailNavigation.js'
      );
      return (await ensureCryptoUpdownDetailEnter(to)) || true;
    },
  },
  {
    path: '/leagues',
    name: 'Leagues',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "leagues" */ '@/views/leagues/index.vue')
    ),
    meta: { titleKey: 'nav.leagues', keepAlive: true, feedScroll: true },
  },
  {
    path: '/leagues/rules',
    name: 'LeagueRules',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "league-rules" */ '@/views/leagues/rules.vue')
    ),
    meta: { titleKey: 'leagueRulesPage.pageTitle' },
  },
  {
    path: '/referrals',
    name: 'Referrals',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "referrals" */ '@/views/referrals/index.vue')
    ),
    meta: { titleKey: 'nav.invite', keepAlive: true, feedScroll: true },
  },
  {
    path: '/create-market',
    name: 'CreateMarket',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "create-market" */ '@/views/create-market/index.vue')
    ),
    meta: { requiresAuth: true },
  },
  {
    path: '/create-market/type-guide',
    name: 'CreateMarketTypeGuide',
    component: lazyRoute(() =>
      import(
        /* webpackChunkName: "create-market-type-guide" */ '@/views/create-market/type-guide.vue'
      )
    ),
    meta: { titleKey: 'createMarket.questionType' },
  },
  {
    path: '/get-vant',
    name: 'GetVant',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "get-vant" */ '@/views/get-vant/index.vue')
    ),
  },
  {
    path: '/earn-dscoin',
    name: 'EarnDscoin',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "earn-dscoin" */ '@/views/earn-dscoin/index.vue')
    ),
    meta: { requiresAuth: true, titleKey: 'earnDscoinPage.pageTitle' },
  },
  {
    path: '/trial-fund/sim',
    name: 'TrialFundSim',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "trial-fund-sim" */ '@/views/trial-fund/sim-market.vue')
    ),
    meta: { titleKey: 'trialFund.simPageTitle' },
  },
  {
    path: '/withdraw',
    redirect: { name: 'Account', query: { withdraw: '1' } },
  },
  {
    path: '/account',
    name: 'Account',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "account" */ '@/views/account/index.vue')
    ),
    meta: { requiresAuth: true, keepAlive: true },
  },
  {
    path: '/account/assets',
    redirect: (to) => ({ name: 'Account', query: to.query, hash: to.hash }),
  },
  {
    path: '/account/positions',
    name: 'AccountPositions',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "account-positions" */ '@/views/account/positions.vue')
    ),
    meta: { requiresAuth: true, titleKey: 'accountPositionsPage.title' },
  },
  {
    path: '/account/points',
    name: 'AccountPoints',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "account-points" */ '@/views/account/points.vue')
    ),
    meta: { requiresAuth: true, titleKey: 'accountPointsPage.title' },
  },
  {
    path: '/notifications',
    name: 'NotificationsCenter',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "notifications-center" */ '@/views/notifications/index.vue')
    ),
    meta: { requiresAuth: true, keepAlive: true },
  },
  {
    path: '/notifications/:id',
    name: 'NotificationDetail',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "notification-detail" */ '@/views/notifications/detail.vue')
    ),
    meta: { requiresAuth: true },
  },
  {
    path: '/deposit/onramp-return',
    name: 'DepositOnrampReturn',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "deposit-onramp-return" */ '@/views/deposit/onramp-return.vue')
    ),
  },
  {
    path: '/auth/privy-callback',
    name: 'PrivyCallback',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "privy-callback" */ '@/views/auth/privy-callback.vue')
    ),
  },
  {
    path: '/help',
    name: 'Help',
    component: lazyRoute(() => import(/* webpackChunkName: "help" */ '@/views/help/index.vue')),
    meta: { titleKey: 'helpPage.pageTitle' },
  },
  {
    path: '/help/doc/:slug',
    name: 'HelpDocument',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "help-doc" */ '@/views/help/document.vue')
    ),
    meta: { titleKey: 'helpPage.pageTitle', fullscreen: true },
  },
  {
    path: '/help/market-rules',
    name: 'HelpMarketRules',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "help-market-rules" */ '@/views/help/market-rules.vue')
    ),
    meta: { titleKey: 'marketRules.pageTitle', fullscreen: true },
  },
  {
    path: '/download',
    name: 'AppDownload',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "app-download" */ '@/views/download/index.vue')
    ),
    meta: { titleKey: 'appDownload.pageTitle' },
  },
  {
    path: '/download/go',
    name: 'AppDownloadGo',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "app-download-go" */ '@/views/download/go.vue')
    ),
    meta: {
      titleKey: 'appDownload.backupGoTitle',
      fullscreen: true,
      quietShell: true,
      hidePwaBanner: true,
    },
  },
  {
    path: '/pwa',
    name: 'PwaLanding',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "pwa-landing" */ '@/views/pwa/index.vue')
    ),
    meta: {
      titleKey: 'pwaLanding.pageTitle',
      fullscreen: true,
      hidePwaBanner: true,
      quietShell: true,
    },
  },
  {
    path: '/pwa/tutorial',
    name: 'PwaTutorial',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "pwa-tutorial" */ '@/views/pwa/tutorial.vue')
    ),
    meta: {
      titleKey: 'pwaLanding.tutorialPageTitle',
      fullscreen: true,
      hidePwaBanner: true,
      quietShell: true,
    },
  },
  {
    path: '/profile/:userId',
    name: 'UserProfile',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "user-profile" */ '@/views/profile/index.vue')
    ),
    meta: { titleKey: 'userProfile.pageTitle' },
  },
  {
    path: '/search',
    name: 'Search',
    component: lazyRoute(() => import(/* webpackChunkName: "search" */ '@/views/search/index.vue')),
    meta: { titleKey: 'searchPage.pageTitle', keepAlive: true },
  },
  {
    path: '/watchlist',
    name: 'Watchlist',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "watchlist" */ '@/views/watchlist/index.vue')
    ),
    meta: {
      titleKey: 'watchlistPage.title',
      requiresAuth: true,
      keepAlive: true,
      feedScroll: true,
    },
  },
  {
    path: '/topic/:id',
    name: 'HotTopicDetail',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "topic-detail" */ '@/views/topic/detail.vue')
    ),
    meta: { titleKey: 'hotTopicDetail.pageTitle' },
  },
  {
    path: '/breaking/:id',
    name: 'BreakingNewsDetail',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "breaking-detail" */ '@/views/breaking/detail.vue')
    ),
    meta: { titleKey: 'breakingNewsDetail.pageTitle' },
  },
  {
    path: '/breaking',
    name: 'Breaking',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "breaking" */ '@/views/breaking/index.vue')
    ),
    meta: { titleKey: 'dashboard.breakingTitle', keepAlive: true },
  },
  /** 未匹配路径兜底（须放在 routes 末尾） */
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: lazyRoute(() =>
      import(/* webpackChunkName: "not-found" */ '@/views/not-found/index.vue')
    ),
    meta: { titleKey: 'notFoundPage.title' },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (to.meta?.scrollToTop) return { top: 0, left: 0 };
    if (savedPosition) return savedPosition;
    if (to.hash) {
      return new Promise((resolve) => {
        const tryScroll = (attempt = 0) => {
          const el = document.querySelector(to.hash);
          if (el) {
            resolve({ el: to.hash, behavior: 'smooth' });
            return;
          }
          if (attempt >= 20) {
            resolve({ top: 0, left: 0 });
            return;
          }
          setTimeout(() => tryScroll(attempt + 1), 50);
        };
        tryScroll();
      });
    }
    return { top: 0, left: 0 };
  },
});

function shouldTrackNavigationProgress(to, from) {
  if (consumeNavigationProgressSkip()) return false;
  if (!from?.name && !from?.matched?.length) {
    return Boolean(to.meta?.navProgressCritical);
  }
  if (
    to.name === from.name &&
    to.path === from.path &&
    String(to.params?.id ?? to.params?.matchId ?? to.params?.marketId ?? '') ===
      String(from.params?.id ?? from.params?.matchId ?? from.params?.marketId ?? '')
  ) {
    return false;
  }
  return true;
}

router.beforeEach((to, from) => {
  // 进入 /pwa 等安静壳：立刻关营销弹窗，避免懒加载期间仍显示首页弹层
  if (to.meta?.quietShell) {
    silenceQuietShellPrompts();
  }

  captureInviteCodeFromRouteQuery(to.query);
  captureAcquisitionFromRoute(to);
  const queryWithoutInvite = stripInviteCodeFromQuery(to.query);
  if (queryWithoutInvite) {
    return {
      path: to.path,
      query: queryWithoutInvite,
      hash: to.hash,
      replace: true,
    };
  }

  if (to.meta.requiresAuth && !validateAuthSession()) {
    return { name: 'Home', replace: true };
  }

  if (shouldTrackNavigationProgress(to, from)) {
    startNavigationProgress();
  }

  return true;
});

router.afterEach((to) => {
  finishOrHoldNavigationProgress(Boolean(to.meta?.navProgressCritical));
});

router.onError(() => {
  finishOrHoldNavigationProgress(false);
});

setupChunkLoadRecovery(router);

export default router;
